import EditHeader from 'common/components/EditHeader/EditHeader';
import HeaderButtonsEdit from 'common/components/EditHeader/HeaderButtonsEdit';
import FormControl from 'common/components/FormControl/FormControl';
import FormScrollError from 'common/components/FormScrollError/FormScrollError';
import useRouter from 'common/hooks/use-router';
import { useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import arrayMutators from 'final-form-arrays';
import { Content } from 'types/Content';
import CategorySelect from 'components/select/CategorySelect/CategorySelect';
import ProfileSelect from 'components/select/ProfileSelect/ProfileSelect';
import ContentStatusSelect from 'components/select/ContentStatusSelect/ContentStatusSelect';
import useAuth from 'common/hooks/useAuth';

type ContentFormProps = {
  content: Content | undefined;
  onSubmit: (values: any) => any;
};

function ContentForm({ onSubmit, content }: ContentFormProps) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState('');
  const { navigate } = useRouter();
  const { user } = useAuth();

  const initialValues = useMemo(
    () =>
      content?.id
        ? {
            ...content,
            category: content.category?.id,
            authorOperator: content.authorOperator?.id,
          }
        : {
            title: '',
            description: '',
            category: null,
            authorOperator: null,
            status: 'draft',
            images: [],
          },
    [content]
  );

  const onSubmitWrapper = async (values: any) => {
    const res = await onSubmit(values);

    if (res.message) {
      setErrorMessage(res.message);
    }
  };

  return (
    <Form
      onSubmit={onSubmitWrapper}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([f]: any, state: any, utils: any) => {
          utils.changeValue(state, f.field, () => f.value);
        },
      }}
      render={({ handleSubmit, values }) => {
        return (
          <div className="mx-auto max-w-full py-6 px-6 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={t(content?.id ? 'editItem.content' : 'newItem.content')}
              subTitle={values.name}
              icon="HandThumbUpIconSolid"
              errorMessage={errorMessage}
              HeaderButtonsEdit={
                <HeaderButtonsEdit
                  inHeader
                  onSubmit={handleSubmit}
                  onCancel={() => navigate('/contents')}
                />
              }
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormScrollError />

              <div className="responsive-flex gap-6 mt-2">
                <div className="md:w-2/3 flex flex-col gap-6">
                  <div className="white-box flex flex-col w-full gap-2">
                    <FormControl name="title" title={t('common.title')} required />
                    <FormControl name="category" titleKey="forms.category" type="custom">
                      <CategorySelect />
                    </FormControl>
                    <FormControl
                      className="w-full"
                      name="description"
                      type="textarea"
                      titleKey="forms.description"
                      required
                    />
                  </div>
                  <div className="responsive-flex gap-6">
                    <div className="white-box flex flex-col gap-2">
                      <FormControl
                        title={t('forms.mainImage')}
                        labelIcon="PhotoIcon"
                        name="images"
                        type="images"
                        max={1}
                        limit={1}
                      />
                    </div>
                    <div className="white-box flex flex-col flex-1 gap-2">
                      <FormControl
                        title={t('forms.documents')}
                        labelIcon="AttachedFile"
                        name="documents"
                        type="file"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col md:w-1/3 gap-6">
                  <div className="white-box flex flex-col w-full gap-2">
                    <FormControl
                      name="status"
                      titleKey="common.status"
                      type="custom"
                      disabled={!user?.isAdmin || !user.isSuperAdmin}
                    >
                      <ContentStatusSelect />
                    </FormControl>

                    {content?.id && (
                      <>
                        <FormControl
                          name="publishedDate"
                          titleKey="forms.publishedDate"
                          type="date"
                        />
                        <FormControl
                          name="endPublishedDate"
                          titleKey="forms.endPublishedDate"
                          type="date"
                        />
                      </>
                    )}
                  </div>

                  {user?.isAdmin && (
                    <div className="white-box flex flex-col w-full gap-2">
                      <FormControl name="authorOperator" titleKey="forms.author" type="custom">
                        <ProfileSelect />
                      </FormControl>
                    </div>
                  )}
                </div>
              </div>
              <HeaderButtonsEdit onSubmit={handleSubmit} onCancel={() => navigate('/contents')} />
            </form>
          </div>
        );
      }}
    />
  );
}

export default ContentForm;
